import {t} from "@lingui/core/macro";
import moment from "moment";

import {getGuideActivitySlug} from "@khan/guide-core-helpers";
import {AIActivity} from "@khan/guide-types";

import type {
    guideTranscript_aiGuideThread_coeditingDocument_snapshots as CoeditingSnapshot,
    guideTranscript_aiGuideThread_coeditingDocument_snapshots_text as CoeditorState,
    guideTranscript_aiGuideThread_interactions as Interaction,
} from "./__graphql-types__/guideTranscript.ts";
import type {guideTranscripts_aiGuideThreads_threads as ThreadType} from "./__graphql-types__/guideTranscripts.ts";

export const canResumeActivity = (pathname: string) => {
    const activitySlug = getGuideActivitySlug(pathname);
    // Activities that do not have resume chat fully functional
    // yet should not be allowed to resume
    return ![
        AIActivity.DefaultTeacher,
        AIActivity.DefaultLearner,
        AIActivity.ClassSnapshot,
        AIActivity.AssignmentRecommendations,
        AIActivity.ChatSummaryTeacher,
        AIActivity.ChatSummaryParent,
        AIActivity.WriteClassNewsletter,
        AIActivity.WriteRecommendationLetter,
        AIActivity.AcademicEssayFeedback,
        AIActivity.EssayFeedbackPitfalls,
    ].includes(activitySlug as AIActivity);
};

export const getResumeUrl = (url: string, threadId: string): string => {
    const urlParts = url.split("?");
    const baseUrl = urlParts[0];
    const queryString = urlParts.length > 1 ? urlParts[1] : "";
    const urlSearchParams = new URLSearchParams(queryString);

    // Add resumeThreadId for the resume thread link
    urlSearchParams.set("resumeThreadId", threadId);
    // The embedded param is used to indicate that an activity should be
    // rendered for the mobile app. Conversations had on the mobile app
    // will have this param stored in their URL, so delete the param to allow
    // for resumed mobile app conversations to render with proper layout on web.
    // TODO (GL-1410) Work will be needed here for resuming chromse extension convos.
    urlSearchParams.delete("embedded");
    return `${baseUrl}?${urlSearchParams.toString()}`;
};

type InteractionWithCoeditorState = Interaction & {
    questionCoeditorState?: CoeditorState;
    answerCoeditorState?: CoeditorState;
};

export const combineInteractionsAndSnapshots = (
    interactions: Array<Interaction>,
    snapshots: Array<CoeditingSnapshot> | null,
): Array<InteractionWithCoeditorState> => {
    // We need to sort the interactions, they aren't guaranteed to be in a
    // specific order coming from the server.
    const sortedInteractions = interactions
        .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
        .filter((interaction) => interaction.question || interaction.answer);

    // We're currently assuming that the first interaction holds the initial
    // message and the second interaction contains the trigger message, so we
    // shouldn't have a snapshot before the second interaction.
    if (!snapshots || snapshots.length === 0 || sortedInteractions.length < 2) {
        return sortedInteractions;
    }

    // Snapshots are returned newest to oldest
    // It's possible we saved a snapshot before the interaction with the
    // coediting trigger was saved. However, the earliest a snapshot should be
    // attached is on the second interaction answer.
    const interactionsWithCoeditorState: Array<InteractionWithCoeditorState> = [
        sortedInteractions[0],
    ];

    const oldestSnapshot = snapshots[snapshots.length - 1];
    if (sortedInteractions.length === 2) {
        // There's only two interactions and we have a snapshot, so we assume
        // the second interaction contains the coediting trigger and attach the
        // snapshot to its answer.
        interactionsWithCoeditorState.push({
            ...sortedInteractions[1],
            answerCoeditorState: oldestSnapshot.text,
        });

        return interactionsWithCoeditorState;
    }

    // There's more than two interactions. If the first snapshot is created
    // before the third interaction, we can assume the second interaction
    // contained the coediting trigger and attach the snapshot to its answer.
    if (
        Date.parse(oldestSnapshot.createdAt) <
        Date.parse(sortedInteractions[2].createdAt)
    ) {
        interactionsWithCoeditorState.push({
            ...sortedInteractions[1],
            answerCoeditorState: oldestSnapshot.text,
        });
    } else {
        interactionsWithCoeditorState.push({
            ...sortedInteractions[1],
        });
    }

    for (let i = 2; i < sortedInteractions.length; i++) {
        const interaction = sortedInteractions[i];
        // The snapshot associated with the question is the one that was
        // created right before the interaction and after the previous
        // interaction
        const questionCoeditorState = snapshots?.find(
            (snapshot) =>
                Date.parse(snapshot.createdAt) <
                    Date.parse(interaction.createdAt) &&
                Date.parse(snapshot.createdAt) >
                    Date.parse(sortedInteractions[i - 1]?.createdAt ?? ""),
        )?.text;
        // The snapshot associated with the answer is the one that was
        // created right after the interaction and before the next
        // interaction
        // If there's no next interaction, return the last snapshot where
        // the createdAt is after the current interaction
        const answerCoeditorState = snapshots?.findLast(
            (snapshot) =>
                Date.parse(snapshot.createdAt) >
                    Date.parse(interaction.createdAt) &&
                Date.parse(snapshot.createdAt) <
                    Date.parse(
                        sortedInteractions[i + 1]?.createdAt ?? "1/1/3000",
                    ),
        )?.text;

        interactionsWithCoeditorState.push({
            ...interaction,
            questionCoeditorState,
            answerCoeditorState,
        });
    }

    return interactionsWithCoeditorState;
};

export const ellipsify = (
    text: string,
    maxLength: number,
    direction: "end" | "start",
) => {
    if (text.length <= maxLength) {
        return text;
    }

    if (direction === "start") {
        return "…" + text.substring(text.length - maxLength);
    }

    return text.substring(0, maxLength) + "…";
};

/**
 * Maps a the page type from the query result to a translated string.
 */
export const mapPageTypeResult = (
    pageTypeResult: string | undefined,
): string => {
    let pageType;
    switch (pageTypeResult) {
        case "home":
            pageType = t`Homepage`;
            break;
        case "class":
            pageType = t`Class Dashboard`;
            break;
        case "khanmigo-tools":
            pageType = t`Khanmigo Tools`;
            break;
        case "teacher":
            pageType = t`Teacher Dashboard`;
            break;
        case "video":
            pageType = t`Video`;
            break;
        case "article":
            pageType = t`Article`;
            break;
        case "exercise":
            pageType = t`Exercise`;
            break;
        case "project":
            pageType = t`Project`;
            break;
        case "challenge":
            pageType = t`Challenge`;
            break;
        case "interactive":
            pageType = t`Interactive`;
            break;
        case "talkthrough":
            pageType = t`Talkthrough`;
            break;
        case "unit":
            pageType = t`Unit`;
            break;
        case "course":
            pageType = t`Course`;
            break;
        case "activity":
        case undefined:
        case "default":
        case "*":
        default:
            pageType = "";
    }
    return pageType;
};

/**
 * Maps the page title from the query result to a translated string. For most
 * cases the string result from the backend is already the translatedTitle.
 */
export const maybeMapPageTitle = (
    pageTitleResult: string | undefined,
): string => {
    let pageTitle = pageTitleResult;
    switch (pageTitle) {
        case "Homepage":
            pageTitle = t`Homepage`;
            break;
        case "Class Dashboard":
            pageTitle = t`Class Dashboard`;
            break;
        case "Khanmigo Tools":
            pageTitle = t`Khanmigo Tools`;
            break;
        case "Teacher Dashboard":
            pageTitle = t`Teacher Dashboard`;
            break;
        case undefined:
            pageTitle = "";
            break;
    }
    return pageTitle;
};

/**
 * Returns a formatted string containing the page type and title information
 * from a thread's interactions. Handles all translation and formatting logic
 * in a single function.
 */
export const formatPageInfoString = (thread: ThreadType): string => {
    // Find the first interaction that has page info
    const interactionWithPageInfo = thread.interactions.find(
        (interaction) => interaction.pageType || interaction.pageTitle,
    );

    const pageTypeResult = interactionWithPageInfo?.pageType;
    const pageTitleResult = interactionWithPageInfo?.pageTitle;

    const pageType = mapPageTypeResult(pageTypeResult);
    const pageTitle = maybeMapPageTitle(pageTitleResult);

    // Format the final string
    const isDuplicate =
        pageType === pageTitle || pageTitle.includes(`${pageType}: `);
    return (pageType && !isDuplicate ? pageType + ": " : "") + pageTitle;
};

export const countNumberOfInteractions = (thread: ThreadType) => {
    return thread.interactions.reduce((acc, interaction) => {
        let counter = 0;
        if (interaction.question) {
            counter++;
        }
        return acc + counter;
    }, 0);
};

/**
 * Returns the correct thread group header string.
 *
 * One of "Today", "Yesterday", or the date itself.
 */
export const formatDateHeaderString = (lastUpdatedAt: string): string => {
    const today = moment();
    const yesterday = moment().subtract("1", "day");
    const headerDate = moment(lastUpdatedAt, "l");

    return headerDate.isSame(today, "day")
        ? t`Today`
        : headerDate.isSame(yesterday, "day")
          ? t`Yesterday`
          : headerDate.format("l");
};
